import { Dispatch } from 'redux';
import { Services } from '../../types/services';
import toast from 'react-hot-toast';

const PRACTICE_LOCATIONS_FETCH = 'PRACTICE_LOCATIONS_FETCH';
const PRACTICE_LOCATIONS_FETCH_SUCCESS = 'PRACTICE_LOCATIONS_FETCH_SUCCESS';
const PRACTICE_LOCATIONS_FETCH_FAILURE = 'PRACTICE_LOCATIONS_FETCH_FAILURE';
const PRACTICE_LOCATIONS_CREATE_SUCCESS = 'PRACTICE_LOCATIONS_CREATE_SUCCESS';
const PRACTICE_LOCATIONS_UPDATE_SUCCESS = 'PRACTICE_LOCATIONS_UPDATE_SUCCESS';
const PRACTICE_LOCATIONS_DELETE_SUCCESS = 'PRACTICE_LOCATIONS_DELETE_SUCCESS';
const PATCH_BILLING_REQUESTED = 'PATCH_BILLING_REQUESTED';
const PATCH_BILLING_SUCCEEDED = 'PATCH_BILLING_SUCCEEDED';
const PATCH_BILLING_FAILED = 'PATCH_BILLING_FAILED';

let billingSeqCounter = 0;

interface PracticeLocation {
  id: string;
  locationId: string;
  name: string;
  practiceId: string;
  address?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  locationCategory?: string;
  isBillable?: boolean;
  isBillableOverridden?: boolean;
  source?: string;
  instanceId?: string;
  isDeleted?: boolean;
  deletedDateUtc?: string | null;
  billingError?: string | null;
  billingPending?: boolean;
  billingSeq?: number;
}

interface PatchBillingResponse {
  id: string;
  locationCategory: string;
  isBillable: boolean;
  isBillableOverridden: boolean;
  source: string;
}

interface PracticeLocationsState {
  locations: PracticeLocation[];
  loading: boolean;
  error: string | null;
}

const initialState: PracticeLocationsState = {
  locations: [],
  loading: false,
  error: null,
};

type Action =
  | { type: typeof PRACTICE_LOCATIONS_FETCH }
  | { type: typeof PRACTICE_LOCATIONS_FETCH_SUCCESS; payload: PracticeLocation[] }
  | { type: typeof PRACTICE_LOCATIONS_FETCH_FAILURE; payload: string }
  | { type: typeof PRACTICE_LOCATIONS_CREATE_SUCCESS }
  | { type: typeof PRACTICE_LOCATIONS_UPDATE_SUCCESS }
  | { type: typeof PRACTICE_LOCATIONS_DELETE_SUCCESS }
  | { type: typeof PATCH_BILLING_REQUESTED; payload: { id: string; seq: number } }
  | {
      type: typeof PATCH_BILLING_SUCCEEDED;
      payload: { id: string; seq: number } & PatchBillingResponse;
    }
  | { type: typeof PATCH_BILLING_FAILED; payload: { id: string; seq: number; error: string } };

export const reducer = (state = initialState, action: Action): PracticeLocationsState => {
  switch (action.type) {
    case PRACTICE_LOCATIONS_FETCH:
      return {
        ...state,
        loading: true,
      };
    case PRACTICE_LOCATIONS_FETCH_SUCCESS: {
      // Merge server rows with existing client rows by id, preserving in-flight
      // billingPending so a concurrent billing PATCH is not silently unblocked.
      const pendingById = new Map(
        state.locations
          .filter(loc => loc.billingPending)
          .map(loc => [loc.id, { billingPending: true, billingSeq: loc.billingSeq }]),
      );
      return {
        ...state,
        locations: action.payload.map(loc => {
          const inflight = pendingById.get(loc.id);
          return inflight ? { ...loc, ...inflight } : loc;
        }),
        loading: false,
        error: null,
      };
    }
    case PRACTICE_LOCATIONS_FETCH_FAILURE:
      // Preserve existing locations so a failed includeDeleted=true fetch doesn't
      // wipe rows that were already loaded with the default (live-only) query.
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    case PRACTICE_LOCATIONS_CREATE_SUCCESS:
    case PRACTICE_LOCATIONS_UPDATE_SUCCESS:
    case PRACTICE_LOCATIONS_DELETE_SUCCESS:
      return state;
    case PATCH_BILLING_REQUESTED:
      return {
        ...state,
        locations: state.locations.map(loc =>
          loc.id === action.payload.id
            ? { ...loc, billingError: null, billingPending: true, billingSeq: action.payload.seq }
            : loc,
        ),
      };
    case PATCH_BILLING_SUCCEEDED:
      return {
        ...state,
        locations: state.locations.map(loc =>
          loc.id === action.payload.id && loc.billingSeq === action.payload.seq
            ? {
                ...loc,
                locationCategory: action.payload.locationCategory,
                isBillable: action.payload.isBillable,
                isBillableOverridden: action.payload.isBillableOverridden,
                source: action.payload.source,
                billingError: null,
                billingPending: false,
              }
            : loc,
        ),
      };
    case PATCH_BILLING_FAILED:
      return {
        ...state,
        locations: state.locations.map(loc =>
          loc.id === action.payload.id && loc.billingSeq === action.payload.seq
            ? { ...loc, billingError: action.payload.error, billingPending: false }
            : loc,
        ),
      };
    default:
      return state;
  }
};

export const actions = (services: Services) => {
  const axiosService = services.axios.Axios();

  const fetchPracticeLocations = (practiceId: string, includeDeleted = false) => {
    return async (dispatch: Dispatch) => {
      try {
        dispatch({ type: PRACTICE_LOCATIONS_FETCH });
        const url = includeDeleted
          ? `v1/practicelocations/practice/${practiceId}?includeDeleted=true`
          : `v1/practicelocations/practice/${practiceId}`;
        const response = await axiosService.get(url);
        dispatch({ type: PRACTICE_LOCATIONS_FETCH_SUCCESS, payload: response.data });
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: PRACTICE_LOCATIONS_FETCH_FAILURE, payload: errorMsg });
        toast.error('Failed to load practice locations');
      }
    };
  };

  const createPracticeLocation = (practiceId: string, payload: Partial<PracticeLocation>) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosService.post(`v1/practicelocations/practice/${practiceId}`, payload);
        dispatch({ type: PRACTICE_LOCATIONS_CREATE_SUCCESS });
        dispatch(fetchPracticeLocations(practiceId) as unknown as Parameters<typeof dispatch>[0]);
        toast.success('Location created');
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: PRACTICE_LOCATIONS_FETCH_FAILURE, payload: errorMsg });
        toast.error('Failed to create location');
      }
    };
  };

  const updatePracticeLocation = (
    practiceLocationId: string,
    payload: Partial<PracticeLocation>,
    practiceId: string,
  ) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosService.patch(`v1/practicelocations/${practiceLocationId}`, payload);
        dispatch({ type: PRACTICE_LOCATIONS_UPDATE_SUCCESS });
        dispatch(fetchPracticeLocations(practiceId) as unknown as Parameters<typeof dispatch>[0]);
        toast.success('Location updated');
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: PRACTICE_LOCATIONS_FETCH_FAILURE, payload: errorMsg });
        toast.error('Failed to update location');
      }
    };
  };

  const deletePracticeLocation = (practiceLocationId: string, practiceId: string) => {
    return async (dispatch: Dispatch) => {
      try {
        await axiosService.delete(`v1/practicelocations/${practiceLocationId}`);
        dispatch({ type: PRACTICE_LOCATIONS_DELETE_SUCCESS });
        dispatch(fetchPracticeLocations(practiceId) as unknown as Parameters<typeof dispatch>[0]);
        toast.success('Location deleted');
      } catch (error: unknown) {
        const errorMsg = error instanceof Error ? error.message : 'An unknown error occurred';
        dispatch({ type: PRACTICE_LOCATIONS_FETCH_FAILURE, payload: errorMsg });
        toast.error('Failed to delete location');
      }
    };
  };

  const patchLocationBilling = (
    practiceLocationId: string,
    body: { locationCategory?: string; isBillable?: boolean; clearOverride?: boolean },
  ) => {
    return async (dispatch: Dispatch) => {
      const seq = ++billingSeqCounter;
      dispatch({ type: PATCH_BILLING_REQUESTED, payload: { id: practiceLocationId, seq } });
      try {
        const response = await axiosService.patch(
          `v1/practicelocations/${practiceLocationId}/billing`,
          body,
        );
        dispatch({
          type: PATCH_BILLING_SUCCEEDED,
          payload: { ...(response.data as PatchBillingResponse), id: practiceLocationId, seq },
        });
      } catch (error: unknown) {
        let errorMsg = 'An unknown error occurred';
        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          error.response &&
          typeof error.response === 'object' &&
          'data' in error.response
        ) {
          const data = (error.response as { data: unknown }).data;
          if (typeof data === 'string') {
            errorMsg = data;
          } else if (data && typeof data === 'object') {
            // ProblemDetails / ValidationProblemDetails (ASP.NET Core)
            const d = data as Record<string, unknown>;
            if (typeof d['detail'] === 'string') errorMsg = d['detail'];
            else if (typeof d['title'] === 'string') errorMsg = d['title'];
            else if (typeof d['message'] === 'string') errorMsg = d['message'];
          }
        } else if (error instanceof Error) {
          errorMsg = error.message;
        }
        dispatch({
          type: PATCH_BILLING_FAILED,
          payload: { id: practiceLocationId, seq, error: errorMsg },
        });
      }
    };
  };

  return {
    fetchPracticeLocations,
    createPracticeLocation,
    updatePracticeLocation,
    deletePracticeLocation,
    patchLocationBilling,
  };
};
